import { useCallback, useEffect, useRef } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DesignPackageActivityAction,
  DesignPackageActivityEvent,
  DesignPackageActivityPayload,
} from "@/services/bedrock/dtos/DesignPackageActivity";
import { ActorRef, DesignPackageDetail, ItemStatus, PackageStatus } from "@/services/bedrock/dtos/DesignPackage";
import { Page } from "@/services/bedrock/dtos/Page";
import { designKeys } from "@/services/bedrock/queryKeys";
import { CableStatus, designPackageProgressStore, useCableSubscription } from "@/utils/actioncable";
import { useInterval } from "@/utils/useInterval";
import { isSelfActor, useViewer } from "../viewer";

export type DesignPackageChannelMessage =
  | {
      type: "progress";
      designPackageId: number;
      designPackageItemId: number | null;
      status: PackageStatus | ItemStatus;
      progressStatus: string | null;
      percent: number | null;
      at: string;
    }
  | {
      type: "updated";
      designPackageId: number;
      reason: DesignPackageActivityAction;
      actor: ActorRef;
      designPackageItemId: number | null;
      designPackageItemPosition: number | null;
      designTemplateId: number | null;
      payload: DesignPackageActivityPayload;
      at: string;
    }
  | { type: "activity"; event: DesignPackageActivityEvent }
  | { type: "presence"; viewers: ActorRef[] };

export const DESIGN_PACKAGE_CHANNEL = "DesignPackageChannel";
const INVALIDATE_DEBOUNCE_MS = 250;
const TOAST_THROTTLE_MS = 5_000;
const POLL_INTERVAL_MS = 5_000;
const PRESENCE_INTERVAL_MS = 30_000;
const STATS_REASONS: DesignPackageActivityAction[] = ["published", "unpublished", "rendered_for_player"];

/** Messages for changes made by other people, keyed by the activity actions that toast. */
export const ACTOR_TOASTS = {
  actorRefining: (name: string, slideLabel: string) => `${name} is refining ${slideLabel}…`,
  actorKept: (name: string, slideLabel: string) => `${name} kept a new version of ${slideLabel}`,
  actorDiscarded: (name: string, slideLabel: string) => `${name} discarded the new version of ${slideLabel}`,
  actorPublished: (name: string) => `${name} published this design`,
  actorUnpublished: (name: string) => `${name} unpublished this design`,
  actorPromptUpdated: (name: string) => `${name} updated who receives this design`,
  actorRenamed: (name: string, to: string) => `${name} renamed this design to “${to}”`,
  actorArchived: (name: string) => `${name} archived this design`,
  actorRestored: (name: string) => `${name} restored this design`,
  actorBackgroundDefault: (name: string, slideLabel: string) =>
    `${name} changed the default background of ${slideLabel}`,
  actorBackgroundsGenerated: (name: string, slideLabel: string) => `${name} added new backgrounds to ${slideLabel}`,
  actorBackgroundAdded: (name: string, slideLabel: string) => `${name} added a background to ${slideLabel}`,
  actorBackgroundRemoved: (name: string, slideLabel: string) => `${name} removed a background from ${slideLabel}`,
  actorCustomBackgrounds: (name: string, on: boolean) => `${name} turned player photos ${on ? "on" : "off"}`,
  actorBackgroundPolicy: (name: string) => `${name} changed what players can choose`,
};

export type ToastKey = keyof typeof ACTOR_TOASTS;

/** "slide 2" when the package has several slides and a position is known, otherwise "the design". */
export function slideLabel(position: number | null, slidesCount?: number): string {
  return position == null || slidesCount === 1 ? "the design" : `slide ${position}`;
}

/** Every activity action maps to a toast key, or to null when others' activity is silent. */
export const ACTIVITY_TOASTS: Record<DesignPackageActivityAction, ToastKey | null> = {
  generated: null,
  duplicated: null,
  rendered_for_player: null,
  refined: "actorRefining",
  revision_approved: "actorKept",
  revision_discarded: "actorDiscarded",
  published: "actorPublished",
  unpublished: "actorUnpublished",
  prompt_updated: "actorPromptUpdated",
  renamed: "actorRenamed",
  archived: "actorArchived",
  restored: "actorRestored",
  background_default_changed: "actorBackgroundDefault",
  backgrounds_generated: "actorBackgroundsGenerated",
  background_uploaded: "actorBackgroundAdded",
  background_attached: "actorBackgroundAdded",
  background_removed: "actorBackgroundRemoved",
  background_policy_changed: "actorBackgroundPolicy",
  custom_backgrounds_toggled: "actorCustomBackgrounds",
  // The thread card and the compare view carry an admin's edit; approving at once toasts through revision_approved.
  html_edited: null,
};

const lastToastAt = new Map<string, number>();

/** Test-only reset of the toast throttle. */
export function resetDesignPackageToastThrottle(): void {
  lastToastAt.clear();
}

export function actorDisplayName(actor: ActorRef): string {
  return actor.onBehalfOf ? `${actor.name} (as ${actor.onBehalfOf.name})` : actor.name;
}

function previewsPrefix(id: number, themeId?: number | null) {
  const base = designKeys.previews(id, themeId ?? null);
  return themeId === undefined ? base.slice(0, 2) : base.slice(0, 3);
}

export function buildUpdatedToast(
  message: Extract<DesignPackageChannelMessage, { type: "updated" }>,
  detail: DesignPackageDetail | undefined
): string | null {
  const key = ACTIVITY_TOASTS[message.reason];
  if (!key) return null;

  const name = actorDisplayName(message.actor);
  const payload = message.payload as Record<string, unknown>;
  const refinesAll = payload?.scope === "all";
  const position = refinesAll ? null : message.designPackageItemPosition;
  const label = slideLabel(position, detail?.slidesCount);

  switch (key) {
    case "actorRenamed":
      return ACTOR_TOASTS.actorRenamed(name, String(payload?.to ?? ""));
    case "actorCustomBackgrounds":
      return ACTOR_TOASTS.actorCustomBackgrounds(name, Boolean(payload?.allowCustomBackgrounds));
    case "actorBackgroundPolicy":
    case "actorPublished":
    case "actorUnpublished":
    case "actorPromptUpdated":
    case "actorArchived":
    case "actorRestored":
      return ACTOR_TOASTS[key](name);
    default:
      return ACTOR_TOASTS[key](name, label);
  }
}

function prependActivity(queryClient: QueryClient, id: number, event: DesignPackageActivityEvent) {
  queryClient.setQueryData(designKeys.activity(id), (current: unknown) => {
    if (!current) return current;

    const infinite = current as { pages?: Page<DesignPackageActivityEvent>[]; pageParams?: unknown[] };
    if (Array.isArray(infinite.pages)) {
      const [first, ...rest] = infinite.pages;
      if (!first) return current;
      if (first.content.some(e => e.id === event.id)) return current;
      return { ...infinite, pages: [{ ...first, content: [event, ...first.content] }, ...rest] };
    }

    const page = current as Page<DesignPackageActivityEvent>;
    if (!Array.isArray(page.content)) return current;
    if (page.content.some(e => e.id === event.id)) return current;
    return { ...page, content: [event, ...page.content] };
  });
}

export interface UseDesignPackageChannelOptions {
  enabled?: boolean;
  presence?: boolean;
  /** Theme whose previews the polling fallback refetches while the socket is down. */
  pollThemeId?: number | null;
  /** False keeps the hook from toasting other actors' changes (list pages stay quiet). */
  toasts?: boolean;
  /** Called after the hook has handled each message. */
  onMessage?: (message: DesignPackageChannelMessage) => void;
  /** Called whenever the hook refetches outside a message: on (re)connect and on each poll while the socket is down. */
  onResync?: () => void;
}

/**
 * The single owner of everything a package's channel drives: progress, invalidation, toasts for
 * other actors, presence, reconnect refetch, and the polling fallback. Pages never toast on cable
 * messages themselves.
 */
export function useDesignPackageChannel(
  packageId: number | null,
  opts: UseDesignPackageChannelOptions = {}
): { connection: CableStatus } {
  const { enabled = true, presence = false, pollThemeId, onMessage, onResync, toasts = true } = opts;
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onResyncRef = useRef(onResync);
  onResyncRef.current = onResync;
  const queryClient = useQueryClient();
  const viewer = useViewer();

  const pendingInvalidate = useRef<{ timer: ReturnType<typeof setTimeout> | null; stats: boolean }>({
    timer: null,
    stats: false,
  });

  const flushInvalidate = useCallback(
    (id: number) => {
      const pending = pendingInvalidate.current;
      pending.timer = null;
      const withStats = pending.stats;
      pending.stats = false;

      queryClient.invalidateQueries({ queryKey: designKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: designKeys.activity(id) });
      // Only fetches while the Trace sheet is open; progress messages skip it because it's heavy.
      queryClient.invalidateQueries({ queryKey: designKeys.trace(id) });
      queryClient.invalidateQueries({ queryKey: previewsPrefix(id) });
      if (withStats) queryClient.invalidateQueries({ queryKey: designKeys.stats(id) });
    },
    [queryClient]
  );

  const scheduleInvalidate = useCallback(
    (id: number, includeStats: boolean) => {
      const pending = pendingInvalidate.current;
      pending.stats = pending.stats || includeStats;
      if (pending.timer) return;
      pending.timer = setTimeout(() => flushInvalidate(id), INVALIDATE_DEBOUNCE_MS);
    },
    [flushInvalidate]
  );

  useEffect(() => {
    return () => {
      const pending = pendingInvalidate.current;
      if (pending.timer) clearTimeout(pending.timer);
      pending.timer = null;
      pending.stats = false;
    };
  }, [packageId]);

  const applyMessage = useCallback(
    (message: DesignPackageChannelMessage) => {
      if (packageId == null || !message || typeof message !== "object") return;

      switch (message.type) {
        case "progress": {
          if (message.status === "completed" || message.status === "failed") {
            // A slide finishing its preview (after a refine, say) logs no activity, so no `updated`
            // follows; refetch here or the cached slide stays "rendering" until a reload.
            scheduleInvalidate(packageId, false);
            if (message.designPackageItemId == null) designPackageProgressStore.clear(packageId);
            else
              designPackageProgressStore.set(packageId, {
                status: message.status,
                progressStatus: message.progressStatus,
                percent: null,
                designPackageItemId: message.designPackageItemId,
                at: message.at,
              });
          } else {
            designPackageProgressStore.set(packageId, {
              status: message.status,
              progressStatus: message.progressStatus,
              percent: message.percent,
              designPackageItemId: message.designPackageItemId,
              at: message.at,
            });
          }
          return;
        }
        case "updated": {
          scheduleInvalidate(packageId, STATS_REASONS.includes(message.reason));

          if (!toasts) return;
          // No toasts for the viewer's own changes, impersonating or not.
          if (isSelfActor(message.actor, viewer)) return;

          const throttleKey = `${packageId}:${message.reason}:${message.designPackageItemId ?? "pkg"}`;
          const now = Date.now();
          const last = lastToastAt.get(throttleKey);
          if (last != null && now - last < TOAST_THROTTLE_MS) return;

          const detail = queryClient.getQueryData<DesignPackageDetail>(designKeys.detail(packageId));
          const text = buildUpdatedToast(message, detail);
          if (!text) return;
          lastToastAt.set(throttleKey, now);
          toast.info(text);
          return;
        }
        case "activity":
          prependActivity(queryClient, packageId, message.event);
          return;
        case "presence":
          queryClient.setQueryData(designKeys.presence(packageId), message.viewers);
          return;
        default:
          return;
      }
    },
    [packageId, viewer, queryClient, scheduleInvalidate, toasts]
  );

  const handleMessage = useCallback(
    (message: DesignPackageChannelMessage) => {
      applyMessage(message);
      if (packageId != null && message && typeof message === "object") onMessageRef.current?.(message);
    },
    [applyMessage, packageId]
  );

  // Runs on every (re)connect: messages sent before the subscription was confirmed never arrive.
  const resync = useCallback(() => {
    if (packageId == null) return;
    queryClient.invalidateQueries({ queryKey: designKeys.detail(packageId) });
    queryClient.invalidateQueries({ queryKey: designKeys.activity(packageId) });
    queryClient.invalidateQueries({ queryKey: designKeys.trace(packageId) });
    queryClient.invalidateQueries({ queryKey: previewsPrefix(packageId) });
    queryClient.invalidateQueries({ queryKey: designKeys.presence(packageId) });
    onResyncRef.current?.();
  }, [packageId, queryClient]);

  const subscriptionEnabled = enabled && packageId != null;

  const { status, perform } = useCableSubscription<DesignPackageChannelMessage>({
    channel: DESIGN_PACKAGE_CHANNEL,
    params: { id: packageId },
    enabled: subscriptionEnabled,
    onMessage: handleMessage,
    onConnected: () => {
      resync();
      if (presence) perform("appear");
    },
    onReconnect: () => {
      resync();
      if (presence) perform("appear");
    },
  });

  useInterval(
    () => perform("appear"),
    presence && subscriptionEnabled && status === "connected" ? PRESENCE_INTERVAL_MS : undefined
  );

  useInterval(
    () => {
      if (packageId == null) return;
      queryClient.invalidateQueries({ queryKey: designKeys.detail(packageId) });
      queryClient.invalidateQueries({ queryKey: designKeys.activity(packageId) });
      queryClient.invalidateQueries({ queryKey: previewsPrefix(packageId, pollThemeId ?? null) });
      onResyncRef.current?.();
    },
    subscriptionEnabled && status !== "connected" ? POLL_INTERVAL_MS : undefined
  );

  return { connection: status };
}
